import { BaseTexture, CLEAR_MODES, Filter, FilterSystem, Mesh, MeshGeometry, Rectangle, Renderer, RenderTexture, SCALE_MODES, Shader } from "pixi.js"

// Texture coordinates from the source texture's own size: pooled textures at other resolutions can differ
// in logical size from the filter's input, which the global `inputSize` describes. `uShift` moves the
// drawn quad without moving what it samples (the snap of the last pass).
const VERTEX = `
attribute vec2 aVertexPosition;
uniform mat3 projectionMatrix;
uniform vec4 outputFrame;
uniform vec2 uSourceScale;
uniform vec2 uShift;
varying vec2 vTextureCoord;

void main(void) {
    vec2 position = aVertexPosition * max(outputFrame.zw, vec2(0.)) + outputFrame.xy - uShift;
    gl_Position = vec4((projectionMatrix * vec3(position, 1.0)).xy, 0.0, 1.0);
    vTextureCoord = aVertexPosition * uSourceScale;
}`

const COPY = `
varying vec2 vTextureCoord;
uniform sampler2D uSampler;
uniform vec4 uClamp;

void main(void) {
    gl_FragColor = texture2D(uSampler, clamp(vTextureCoord, uClamp.xy, uClamp.zw));
}`

// pixi's own 5-tap kernel: one pass spreads by 1.29 taps.
const GAUSS = `
varying vec2 vTextureCoord;
uniform sampler2D uSampler;
uniform vec4 uClamp;
uniform vec2 uStep;

vec4 tap(float i) { return texture2D(uSampler, clamp(vTextureCoord + i * uStep, uClamp.xy, uClamp.zw)); }

void main(void) {
    gl_FragColor = (tap(-2.0) + tap(2.0)) * 0.153388 + (tap(-1.0) + tap(1.0)) * 0.221461 + tap(0.0) * 0.250301;
}`

// The blurred storey laid over the screen through a cubic B-spline, from four bilinear taps (four texels a side).
// Bilinear alone left a ridge at every texel edge of a halved texture (faint stripes four pixels apart), and its
// own softening varied with where the screen's pixels fell between texels, so as the camera glided the storey's
// sharpness pulsed. The B-spline is smooth, and it softens by the same variance (a third of a texel) at any phase.
const BSPLINE = `
varying vec2 vTextureCoord;
uniform sampler2D uSampler;
uniform vec4 uClamp;
uniform vec2 uTexel;

vec4 at(vec2 uv) { return texture2D(uSampler, clamp(uv, uClamp.xy, uClamp.zw)); }

void main(void) {
    vec2 p = vTextureCoord / uTexel - 0.5;
    vec2 i = floor(p), f = p - i, f2 = f * f, f3 = f2 * f;
    vec2 w0 = (1.0 - 3.0 * f + 3.0 * f2 - f3) / 6.0;
    vec2 w1 = (4.0 - 6.0 * f2 + 3.0 * f3) / 6.0;
    vec2 w2 = (1.0 + 3.0 * f + 3.0 * f2 - 3.0 * f3) / 6.0;
    vec2 w3 = f3 / 6.0;
    vec2 g0 = w0 + w1, g1 = w2 + w3;
    vec2 a = (i - 0.5 + w1 / g0) * uTexel, b = (i + 1.5 + w3 / g1) * uTexel;
    gl_FragColor = g0.y * (g0.x * at(a) + g1.x * at(vec2(b.x, a.y))) + g1.y * (g0.x * at(vec2(a.x, b.y)) + g1.x * at(b));
}`

// A kept snapshot laid on the screen the way the filter's last pass lays its result: the same B-spline.
const SNAPSHOT_VERTEX = `
attribute vec2 aVertexPosition;
attribute vec2 aTextureCoord;
uniform mat3 projectionMatrix;
uniform mat3 translationMatrix;
varying vec2 vTextureCoord;

void main(void) {
    gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
    vTextureCoord = aTextureCoord;
}`

/** A storey's blur kept between frames: the blurred picture (LINEAR) and the area it covers, in global logical px. */
export interface FocusSnapshot {
    texture: RenderTexture
    frame: Rectangle
    /** Where the storey stood (its world origin, global logical px) when the snapshot was made. */
    origin: { x: number; y: number }
    /** What it was made with: the blur, its resolution, the storey's alpha and cutaway (see BoundedStoreyContainer). */
    key: string
    madeAt: number
}

/** A snapshot's texture grows in steps of this many texels. */
const SNAPSHOT_STEP = 128

const KERNEL_SPREAD = 1.29
/** The widest step between taps, in texels, before a blur shows its taps as ghosts. */
const MAX_TAP_STEP = 1.5
/** The B-spline's own softening, and the 2x2 averaging of the halvings, as variance in final texels. */
const BSPLINE_VARIANCE = 1 / 3
const HALVING_VARIANCE = 1 / 12
const MAX_HALVINGS = 3
/**
 * The texels a focus area's edges keep to, from the storey's origin: every halving then covers it exactly.
 * Off this grid, a half or quarter texture's viewport was rounded, the storey came back scaled a little,
 * and as the area's width changed with the camera the storey jumped by up to four pixels.
 */
export const FOCUS_ALIGN = 2 ** MAX_HALVINGS

/**
 * How far to move a blurred storey laid at `at` (logical px) so it lands on the screen's pixels as sharp art
 * does: a nearest-sampled edge at p falls on the pixel boundary ceil(p - 0.5). Gliding by fractions while the
 * sharp art beside it stepped whole pixels, the two swam against each other at every step of the camera.
 */
export const snapShift = (at: number, resolution: number): number => at - Math.ceil(at * resolution - 0.5) / resolution

/**
 * How to blur by `sigma` texels of the input: halve it (a pass each) until one gaussian pass in each direction
 * makes up the rest with its taps close enough, then the passes and their step, in texels of that texture.
 * A halving costs one render-target switch and saves two, so a wide blur is halved rather than repeated.
 * The final B-spline's softening counts toward the blur.
 */
export const focusPlan = (sigma: number): { halvings: number; passes: number; step: number } => {
    const reach = KERNEL_SPREAD * MAX_TAP_STEP
    const gaussAt = (halvings: number) =>
        Math.sqrt(Math.max(0, (sigma / 2 ** halvings) ** 2 - BSPLINE_VARIANCE - (halvings ? HALVING_VARIANCE : 0)))
    let halvings = 0
    while (halvings < MAX_HALVINGS && gaussAt(halvings) > reach) halvings++
    const gauss = gaussAt(halvings)
    const passes = Math.max(1, Math.ceil((gauss / reach) ** 2))
    return { halvings, passes, step: gauss / (KERNEL_SPREAD * Math.sqrt(passes)) }
}

class Pass extends Filter {
    constructor(fragment: string) {
        super(VERTEX, fragment, { uSourceScale: new Float32Array(2), uClamp: new Float32Array(4), uStep: new Float32Array(2),
            uTexel: new Float32Array(2), uShift: new Float32Array(2) })
    }

    run(filterManager: FilterSystem, from: RenderTexture, to: RenderTexture, clear: CLEAR_MODES, step?: [number, number],
        shift: readonly [number, number] = [0, 0]): void {
        // The area being filtered, in logical px (pixi keeps it protected; it is the `outputFrame` shaders get).
        const frame = (filterManager as unknown as { activeState: { sourceFrame: Rectangle } }).activeState.sourceFrame
        const sx = frame.width / from.width, sy = frame.height / from.height
        const px = 1 / from.baseTexture.realWidth, py = 1 / from.baseTexture.realHeight
        this.uniforms.uSourceScale[0] = sx
        this.uniforms.uSourceScale[1] = sy
        this.uniforms.uClamp[0] = px / 2
        this.uniforms.uClamp[1] = py / 2
        this.uniforms.uClamp[2] = sx - px / 2
        this.uniforms.uClamp[3] = sy - py / 2
        this.uniforms.uTexel[0] = px
        this.uniforms.uTexel[1] = py
        this.uniforms.uShift[0] = shift[0]
        this.uniforms.uShift[1] = shift[1]
        if (step) {
            this.uniforms.uStep[0] = step[0] * px
            this.uniforms.uStep[1] = step[1] * py
        }
        filterManager.applyFilter(this, from, to, clear)
    }
}

/**
 * The out-of-focus blur of a storey above the player. `sigma` is its standard deviation in logical px.
 *
 * The game samples every texture NEAREST (index.ts), pixi's pooled filter textures included. pixi's
 * BlurFilter drew the storey into a texture coarser than the screen, blurred it with taps that snapped to
 * whole texels, and stretched it back over the screen block by block; which texel won changed as the camera
 * moved, so the storey shimmered as the player walked. This filter takes the storey over an area its
 * container anchors to the storey's art (BoundedStoreyContainer), so the art fills the same texels every
 * frame, at the coarsest resolution its blur allows (`focus`); samples its textures LINEAR while it runs;
 * blurs, and lays the result over the screen through a cubic B-spline (BSPLINE), snapped to the screen's
 * pixels (snapShift): the same image every frame, stepping a whole pixel at a time with the sharp art around it.
 */
export class StoreyFocusFilter extends Filter {
    private copy = new Pass(COPY)
    private gauss = new Pass(GAUSS)
    private bspline = new Pass(BSPLINE)
    private _sigma = 0
    /** How far past the art the blur reaches, in logical px: the container widens its area by this. */
    margin = 0
    /** The resolution of the screen the result is laid on (`resolution` is the storey's, see `focus`). */
    screenResolution = 1
    /** The last blurred picture kept (`keep`), to be laid down again on frames that do not redraw the storey. */
    snapshot?: FocusSnapshot
    /** What the storey shows, from its renderer: its art's version (a change redraws at once), and when anything on it last moved. */
    contentVersion = ""
    contentChangedAt = 0
    /** Set for a frame that redraws the storey: apply() keeps its blurred picture as the snapshot, with this key. */
    keep?: { key: string; at: number; origin: { x: number; y: number } }
    private quad?: Mesh

    constructor() {
        super()
        // The container hands this filter its area, already widened and anchored (BoundedStoreyContainer).
        this.autoFit = false
        this.padding = 0
    }

    get sigma(): number { return this._sigma }
    set sigma(value: number) {
        this._sigma = value
        this.margin = Math.ceil(3 * value)
    }

    /**
     * Blur by `sigma` logical px for a screen at `screenResolution`. A blur wide enough to be worked on a halved
     * texture takes the storey at that size to begin with, rather than drawing it whole and averaging it down:
     * behind a nine-storey house, eight storeys drawn at full size cost the GPU as much again as the scene.
     * A quarter or a sixteenth of the pixels and no halving passes; anchored to the art, the coarser draw is still
     * the same every frame, and what its nearest sampling drops is lost in the blur.
     */
    focus(sigma: number, screenResolution: number): void {
        this.sigma = sigma
        this.screenResolution = screenResolution
        this.resolution = screenResolution / 2 ** focusPlan(sigma * screenResolution).halvings
    }

    apply(filterManager: FilterSystem, input: RenderTexture, output: RenderTexture, clearMode: CLEAR_MODES): void {
        const resolution = input.baseTexture.resolution
        const { halvings, passes, step } = focusPlan(this._sigma * resolution)
        const borrowed: RenderTexture[] = []
        const modes = new Map<BaseTexture, SCALE_MODES>()
        const smooth = (texture: RenderTexture) => {
            if (!modes.has(texture.baseTexture)) modes.set(texture.baseTexture, texture.baseTexture.scaleMode)
            texture.baseTexture.scaleMode = SCALE_MODES.LINEAR
            return texture
        }
        const texture = (scale: number) => {
            const next = smooth(filterManager.getFilterTexture(input, resolution / scale))
            borrowed.push(next)
            return next
        }
        smooth(input)
        let source = input
        for (let i = 1; i <= halvings; i++) {
            const half = texture(2 ** i)
            this.copy.run(filterManager, source, half, CLEAR_MODES.CLEAR)
            source = half
        }
        if (step > 0) {
            const spare = texture(2 ** halvings)
            for (let i = 0; i < passes; i++) {
                this.gauss.run(filterManager, source, spare, CLEAR_MODES.CLEAR, [step, 0])
                this.gauss.run(filterManager, spare, source, CLEAR_MODES.CLEAR, [0, step])
            }
        }
        const frame = (filterManager as unknown as { activeState: { sourceFrame: Rectangle } }).activeState.sourceFrame
        if (this.keep) this.keepSnapshot(filterManager, source, frame)
        this.bspline.run(filterManager, source, output, clearMode, undefined,
            [snapShift(frame.x, this.screenResolution), snapShift(frame.y, this.screenResolution)])
        modes.forEach((mode, base) => { base.scaleMode = mode })
        borrowed.forEach(texture => filterManager.returnFilterTexture(texture))
    }

    /**
     * Lay the kept snapshot down again where the storey now stands: `x`, `y` is its area's corner in global
     * logical px. One quad, no pass of its own, nothing of the storey drawn: what a frame costs between redraws.
     */
    drawSnapshot(renderer: Renderer, x: number, y: number): void {
        const snapshot = this.snapshot
        if (!snapshot) return
        const { width, height } = snapshot.frame
        const realWidth = snapshot.texture.baseTexture.realWidth, realHeight = snapshot.texture.baseTexture.realHeight
        // The picture fills the texture's top-left corner: the texture is kept with room to spare (keepSnapshot).
        const u = width / snapshot.texture.width, v = height / snapshot.texture.height
        if (!this.quad) {
            const geometry = new MeshGeometry(new Float32Array(8), new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), new Uint16Array([0, 1, 2, 0, 2, 3]))
            const shader = Shader.from(SNAPSHOT_VERTEX, BSPLINE, { uSampler: snapshot.texture, uTexel: new Float32Array(2), uClamp: new Float32Array(4) })
            this.quad = new Mesh(geometry, shader as never)
        }
        const quad = this.quad, uniforms = quad.shader.uniforms
        const vertices = quad.geometry.getBuffer("aVertexPosition"), uvs = quad.geometry.getBuffer("aTextureCoord")
        const data = vertices.data as Float32Array, uvData = uvs.data as Float32Array
        if (data[2] !== width || data[5] !== height) {
            data.set([0, 0, width, 0, width, height, 0, height])
            vertices.update()
        }
        if (uvData[2] !== u || uvData[5] !== v) {
            uvData.set([0, 0, u, 0, u, v, 0, v])
            uvs.update()
        }
        uniforms.uSampler = snapshot.texture
        uniforms.uTexel[0] = 1 / realWidth
        uniforms.uTexel[1] = 1 / realHeight
        uniforms.uClamp[0] = 0.5 / realWidth
        uniforms.uClamp[1] = 0.5 / realHeight
        uniforms.uClamp[2] = u - 0.5 / realWidth
        uniforms.uClamp[3] = v - 0.5 / realHeight
        const at = (value: number) => value - snapShift(value, this.screenResolution)
        quad.transform.worldTransform.set(1, 0, 0, 1, at(x), at(y))
        quad.worldAlpha = 1
        ;(quad as unknown as { _render(renderer: Renderer): void })._render(renderer)
        renderer.batch.flush()
    }

    dropSnapshot(): void {
        this.snapshot?.texture.destroy(true)
        this.snapshot = undefined
    }

    private keepSnapshot(filterManager: FilterSystem, source: RenderTexture, frame: Rectangle): void {
        const resolution = source.baseTexture.resolution
        let texture = this.snapshot?.texture
        // Kept with room to spare (whole SNAPSHOT_STEP texels), so a redraw as the camera moves reuses it.
        const fits = texture && texture.baseTexture.resolution === resolution && texture.width >= frame.width &&
            texture.height >= frame.height && texture.width <= frame.width + 2 * SNAPSHOT_STEP / resolution
        if (!fits) {
            texture?.destroy(true)
            const size = (logical: number) => Math.ceil(logical * resolution / SNAPSHOT_STEP) * SNAPSHOT_STEP / resolution
            texture = RenderTexture.create({ width: size(frame.width), height: size(frame.height), resolution, scaleMode: SCALE_MODES.LINEAR })
        }
        texture.filterFrame = frame
        this.copy.run(filterManager, source, texture, CLEAR_MODES.CLEAR)
        texture.filterFrame = null
        this.snapshot = { texture, frame: frame.clone(), origin: this.keep.origin, key: this.keep.key, madeAt: this.keep.at }
        this.keep = undefined
    }

    destroy(): void {
        this.copy.destroy()
        this.gauss.destroy()
        this.bspline.destroy()
        this.dropSnapshot()
        this.quad?.destroy()
        super.destroy()
    }
}
